import Vue from 'vue';

export interface ISwReadyState {
    isReady: boolean;
    decided: boolean;
}

export interface ISwReadyOptions {
    timeout?: number;
}

const DEFAULT_TIMEOUT_MS = 500;
const SW_PROBE_PATH = '/__sw_probe__';
const SW_PROBE_HEADER = 'x-sw-probe';

const state: ISwReadyState = Vue.observable({
    isReady: false,
    decided: false
});

const readyWaiters: Array<( ready: boolean ) => void> = [];

let started = false;
let timer: ReturnType<typeof setTimeout> | undefined;
let probePromise: Promise<boolean> | undefined;

const finalize = ( ready: boolean ): void => {

    if ( state.decided && state.isReady === ready ) {

        return;

    }
    state.isReady = ready;
    state.decided = true;
    if ( timer ) {

        clearTimeout( timer );
        timer = undefined;

    }
    while ( readyWaiters.length > 0 ) {

        const waiter = readyWaiters.shift();
        try {

            waiter?.( ready );

        } catch ( e ) {

            console.error( '[swReadyState] waiter throws', e );

        }

    }

};

/**
 * 运行时真实请求失败后会暂时切到 false；
 * 后续只有探针请求被 SW fetch 拦截成功时，才允许恢复到 true。
 */
const markUnavailableInternal = (): void => {

    finalize( false );

};

const markReadyInternal = ( force = false ): void => {

    if ( !force && state.decided && !state.isReady ) {

        return;

    }
    finalize( true );

};

const probeAvailabilityInternal = async (): Promise<boolean> => {

    if ( typeof fetch !== 'function' || typeof navigator === 'undefined' || !( 'serviceWorker' in navigator )) {

        return false;

    }

    if ( probePromise ) {

        return probePromise;

    }

    const sw = navigator.serviceWorker;
    if ( !sw.controller ) {

        return false;

    }

    probePromise = fetch( `${SW_PROBE_PATH}?t=${Date.now()}`, {
        cache: 'no-store',
        credentials: 'same-origin',
        headers: { [SW_PROBE_HEADER]: '1' }
    }).then( response => {

        const ready = response.ok && response.headers.get( SW_PROBE_HEADER ) === '1';
        if ( ready ) {

            markReadyInternal( true );

        }
        return ready;

    }).catch(() => false ).finally(() => {

        probePromise = undefined;

    });

    return probePromise;

};

/**
 * 启动 SW 就绪状态监听：
 * - 监听 `navigator.serviceWorker` 的 `message` 事件，收到 `SW_READY` 且当前页面已被
 *   SW controller 控制时置 ready
 * - 监听 `controllerchange`，controller 变 null 视为不可用；变为可用时主动确认 ready
 * - `timeout` 内未收到 `SW_READY` → 永久 false（启用降级）
 *
 * 重复调用安全（仅首次生效）。
 */
export const startSwReadyState = ( options: ISwReadyOptions = {}): void => {

    if ( started ) {

        return;

    }
    started = true;

    if ( typeof navigator === 'undefined' || !( 'serviceWorker' in navigator )) {

        markUnavailableInternal();
        return;

    }

    const sw = navigator.serviceWorker;

    sw.addEventListener( 'message', ( event ) => {

        const data = event.data;
        if ( !data || typeof data !== 'object' ) {

            return;

        }
        if ( data.type === 'SW_READY' ) {

            if ( sw.controller ) {

                markReadyInternal();

            }

        } else if ( data.type === 'SW_DISABLED' ) {

            markUnavailableInternal();

        }

    });

    sw.addEventListener( 'controllerchange', () => {

        if ( !sw.controller ) {

            markUnavailableInternal();
            return;

        }
        sw.controller.postMessage({ type: 'requestSwReady' });

    });

    if ( sw.controller ) {

        sw.controller.postMessage({ type: 'requestSwReady' });

    }

    const timeout = options.timeout ?? DEFAULT_TIMEOUT_MS;
    timer = setTimeout( markUnavailableInternal, timeout );

};

export const swReadyState = {

    get isReady(): boolean {

        return state.isReady;

    },

    get decided(): boolean {

        return state.decided;

    },

    /**
     * 显式标记 SW 不可用（例如 `registerServiceWorker` 报错时调用）。
     */
    markUnavailable: markUnavailableInternal,

    /**
     * 显式标记 SW 可用（一般无需手工调用，由 `SW_READY` 消息触发）。
     */
    markReady: markReadyInternal,

    /**
     * 等待 SW 状态被决定后返回最终的 ready 值。
     * 若已决定则立刻 resolve；否则等到 `SW_READY` 或超时。
     *
     * @param maxWait 等待上限（毫秒），超过后立即按 false 返回
     */
    await( maxWait?: number ): Promise<boolean> {

        if ( state.decided ) {

            if ( state.isReady ) {

                return Promise.resolve( true );

            }

            return probeAvailabilityInternal();

        }
        return new Promise( resolve => {

            readyWaiters.push( resolve );
            if ( typeof maxWait === 'number' ) {

                setTimeout(() => {

                    const idx = readyWaiters.indexOf( resolve );
                    if ( idx >= 0 ) {

                        readyWaiters.splice( idx, 1 );
                        resolve( state.isReady );

                    }

                }, maxWait );

            }

        });

    }

};

/**
 * 仅供测试使用：重置内部状态。
 *
 * @internal
 */
export const __resetSwReadyStateForTest = (): void => {

    state.isReady = false;
    state.decided = false;
    started = false;
    if ( timer ) {

        clearTimeout( timer );
        timer = undefined;

    }
    readyWaiters.length = 0;
    probePromise = undefined;

};
